import React, { useMemo } from 'react';
import { Activity, AlertTriangle, Database, FileWarning, Image as ImageIcon, RefreshCw, ShieldCheck, Users } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';

const numberFormatter = new Intl.NumberFormat('en-US');

const formatNumber = (value) => numberFormatter.format(Number(value || 0));

const getOverviewValue = (overview, key) => Number(overview?.[key] || 0);

export const AdminOverviewCards = ({
  loading = false,
  overview = null,
  onRefresh,
  onSelectTab,
}) => {
  const metrics = useMemo(() => {
    const totalCases = getOverviewValue(overview, 'total_cases');
    const totalSessions = getOverviewValue(overview, 'total_sessions');
    const needsReview = getOverviewValue(overview, 'needs_review');
    const generationFailures = getOverviewValue(overview, 'generation_failures');
    const imageFailures = getOverviewValue(overview, 'image_failures');
    const llmWarnings = getOverviewValue(overview, 'llm_warnings');

    return [
      {
        label: 'Cases',
        value: totalCases,
        detail: `${formatNumber(totalSessions)} sessions`,
        icon: Database,
        tab: 'cases',
        tone: 'text-sky-300 border-sky-700/50 bg-sky-950/30',
      },
      {
        label: 'Needs Review',
        value: needsReview,
        detail: needsReview > 0 ? 'Open review records' : 'No open reviews',
        icon: Activity,
        tab: 'review',
        tone: needsReview > 0
          ? 'text-amber-300 border-amber-700/50 bg-amber-950/30'
          : 'text-emerald-300 border-emerald-700/50 bg-emerald-950/30',
      },
      {
        label: 'Generation Failures',
        value: generationFailures,
        detail: generationFailures > 0 ? 'Stored diagnostics' : 'No failures stored',
        icon: FileWarning,
        tab: 'failures',
        tone: generationFailures > 0
          ? 'text-red-300 border-red-700/50 bg-red-950/30'
          : 'text-emerald-300 border-emerald-700/50 bg-emerald-950/30',
      },
      {
        label: 'Image Failures',
        value: imageFailures,
        detail: imageFailures > 0 ? 'Prompt or storage issues' : 'Images healthy',
        icon: ImageIcon,
        tab: 'images',
        tone: imageFailures > 0
          ? 'text-orange-300 border-orange-700/50 bg-orange-950/30'
          : 'text-emerald-300 border-emerald-700/50 bg-emerald-950/30',
      },
      {
        label: 'LLM Warnings',
        value: llmWarnings,
        detail: llmWarnings > 0 ? 'Parser and consistency warnings' : 'No warnings',
        icon: AlertTriangle,
        tab: 'logs',
        tone: llmWarnings > 0
          ? 'text-yellow-300 border-yellow-700/50 bg-yellow-950/30'
          : 'text-emerald-300 border-emerald-700/50 bg-emerald-950/30',
      },
      {
        label: 'Admin Health',
        value: needsReview + generationFailures + imageFailures + llmWarnings,
        detail: 'Combined open signals',
        icon: ShieldCheck,
        tab: 'overview',
        tone: needsReview + generationFailures + imageFailures + llmWarnings > 0
          ? 'text-amber-300 border-amber-700/50 bg-amber-950/30'
          : 'text-emerald-300 border-emerald-700/50 bg-emerald-950/30',
      },
    ];
  }, [overview]);

  const healthSignal = metrics[5].value;

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white">Overview</h2>
          <p className="text-sm text-gray-400">
            Case generation, review, image, and warning signals.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={onRefresh}
          disabled={loading}
          className="w-fit border-gray-700 text-gray-200"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Badge className={healthSignal > 0
          ? 'bg-amber-900/50 text-amber-300 border border-amber-700'
          : 'bg-emerald-900/50 text-emerald-300 border border-emerald-700'}
        >
          {healthSignal > 0 ? `${formatNumber(healthSignal)} open signals` : 'No open signals'}
        </Badge>
        <Badge variant="outline" className="border-gray-700 text-gray-300">
          <Users className="w-3 h-3 mr-1" />
          Admin review console
        </Badge>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
        {metrics.map((metric) => {
          const Icon = metric.icon;
          return (
            <Card
              key={metric.label}
              role={metric.tab !== 'overview' ? 'button' : undefined}
              tabIndex={metric.tab !== 'overview' ? 0 : undefined}
              onClick={() => metric.tab !== 'overview' && onSelectTab?.(metric.tab)}
              onKeyDown={(event) => {
                if (metric.tab !== 'overview' && (event.key === 'Enter' || event.key === ' ')) {
                  event.preventDefault();
                  onSelectTab?.(metric.tab);
                }
              }}
              className={`rounded-lg border bg-gray-900/60 shadow-none transition-colors ${metric.tone} ${metric.tab !== 'overview' ? 'hover:bg-white/10 cursor-pointer' : ''}`}
            >
              <CardHeader className="p-4 pb-2">
                <div className="flex items-center justify-between gap-3">
                  <CardTitle className="text-sm font-medium text-gray-300">
                    {metric.label}
                  </CardTitle>
                  <Icon className="w-4 h-4" />
                </div>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                <div className="text-3xl font-semibold text-white">
                  {loading && !overview ? '...' : formatNumber(metric.value)}
                </div>
                <div className="mt-1 text-xs text-gray-400">
                  {metric.detail}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default AdminOverviewCards;
